import React, { useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { initDuckDB } from "./apis/duckdbLoader";
import {
  auth,
  customTokenSignIn,
  getFirebaseAuthToken,
} from "../src/firebase/firebase";
import axios from "axios";
import { getParam } from "./components/auth/Login";
import { postRequest } from "./apis/axiosAction";
import { CircularProgress } from "@mui/material";
import { setUserInfo, clearUserInfo, fetchAuthorityInfo, syncTenpoCodes } from "./data/userInfo";
import {
  generateKeyPairRsaOaep,
  exportPublicKey,
  arrayBufferToBase64,
  decryptRsaOaepAndConcatenate,
} from "./apis/cryptoUtils";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import {
  setAuthLoading,
  setAuthInitialized,
  selectAuthLoading,
  selectAuthInitialized
} from "./features/authSlice";

initDuckDB();


interface pathState {
  mode: string;
  code: string;
  codeKey: string;
  infoKey1: string;
  tenpoName: string;
  infoKey2: string;
  pmaCode: string;
  infoKey3: string;
  pmaName: string;
}

const initial: string = "daily-management";

// URLのパス及びパラメータの調整を行う
const setUrlPath = (): pathState => {
  const url: URL = new URL(window.location.href);
  const searchParams: URLSearchParams = url.searchParams;
  searchParams.delete("token");
  searchParams.delete("application");
  const mode: string =
    getParam("mode", window.location.href) !== ""
      ? getParam("mode", window.location.href)
      : "";
      // : initial;
  const codeKey = mode === "daily-store-multi" ? "docode" : "tenpocode";
  const infoKey1 = mode === "info-class" ? "tenponame" : "";
  const infoKey2 = mode === "info-class" ? "pmacode" : "";
  const infoKey3 = mode === "info-class" ? "pmaname" : "";
  const code: string = getParam(codeKey, window.location.href);
  const tenpoName: string = getParam(infoKey1, window.location.href);
  const pmaCode: string = getParam(infoKey2, window.location.href);
  const pmaName: string = getParam(infoKey3, window.location.href);
  const value: pathState = {
    mode,
    code,
    codeKey,
    infoKey1,
    tenpoName,
    infoKey2,
    pmaCode,
    infoKey3,
    pmaName,
  };
  return value;
};

const App: React.FC = (props: any) => {
  const navigate = useNavigate();
  // Redux状態管理: useStateからReduxのuseAppSelectorに移行
  const loginLoading = useAppSelector(selectAuthLoading);
  const authInitialized = useAppSelector(selectAuthInitialized);
  const ps: pathState = setUrlPath();
  const dispatch = useAppDispatch();
  const search = useLocation().search;
  const query = new URLSearchParams(search);
  const tokenPatamertername = "token";
  const token = query.get(tokenPatamertername);

  useEffect(() => {
    const unSub = auth.onAuthStateChanged(async (authUser) => {
      // Redux: 認証初期化状態を更新
      dispatch(setAuthInitialized(true));

      if (authUser) {
        // firebase認証済み
        // Redux: ローディング状態を更新
        dispatch(setAuthLoading(true));
        try {
          // Cookie/トークン更新はバックグラウンドで実行（ユーザー情報取得は分離）
          callUserAuthAfterInitialLogin(false, true, false, true).catch(
            (error) => console.error("Cookie更新エラー:", error)
          );

          // ユーザー情報取得を先行して実行（ローディング解除の条件）
          await updateUserInfoFromAPI();

          if (ps.mode !== "token" && ps.mode !== "") {
            // window.open はSPAの遷移を壊すため、navigateに置き換え
            const url = new URL(window.location.href);
            const searchParams = url.searchParams;
            searchParams.delete("token");
            searchParams.delete("application");
            navigate(`/${ps.mode}?${searchParams.toString()}`, { replace: true });
          } else {
            // ホーム画面に遷移
            navigate("/home", { replace: true });
          }
        } catch (error) {
          console.error("認証エラー:", error);
          // フォールバック: 直接ユーザー情報を取得
          try {
            await updateUserInfoFromAPI();
            if (ps.mode !== "token" && ps.mode !== "") {
              // window.open はSPAの遷移を壊すため、navigateに置き換え
              const url = new URL(window.location.href);
              const searchParams = url.searchParams;
              searchParams.delete("token");
              searchParams.delete("application");
              navigate(`/${ps.mode}?${searchParams.toString()}`, { replace: true });
            } else {
              // ホーム画面に遷移
              navigate("/home", { replace: true });
            }
          } catch (fallbackError) {
            console.error("フォールバック認証エラー:", fallbackError);
            // 認証に完全に失敗した場合の処理
            if (process.env.REACT_APP_MODE === "develop") {
              navigate("/login", { replace: true });
            } else {
              window.location.href = process.env.REACT_APP_DIGITAL_TOOL!;
            }
          }

        } finally {
          // Redux: ローディング状態を終了
          dispatch(setAuthLoading(false));
        }
      } else {
        // firebase認証未
        // ユーザー情報をクリア
        clearUserInfo();
        
        // 本番環境かどうかを判定（本番環境以外はログイン画面に遷移）
        const isProduction = process.env.REACT_APP_MODE === "production" || process.env.REACT_APP_MODE === "prod";
        
        // if (!isProduction) {
        //   // 本番環境以外（開発・テスト環境）ではログイン画面に遷移
        //   console.log("本番環境以外: ログイン画面に遷移します");
        //   navigate("/login", { replace: true });
        // } else if (token !== null) {
        if (token !== null) {
          // トークンがある場合はカスタム認証を実行
          // Redux: ローディング状態を開始
          dispatch(setAuthLoading(true));
          try {
            await callCustomFirebaseAuthentication(token);
            // Firebase認証成功後は上記のauthUserブロックで処理される
          } catch (error) {
            console.error("カスタム認証エラー:", error);
            // Redux: ローディング状態を終了
            dispatch(setAuthLoading(false));
            // 認証失敗時はデジタルツールに遷移
            window.location.href = process.env.REACT_APP_DIGITAL_TOOL!;
          }
        } else {
          // Firebase認証されず、かつデジタルツールから遷移せずにトークンがない場合はCookie経由で認証にトライする
          try {
            await callUserAuthAfterInitialLogin(true, false, false, false);
          } catch (error) {
            console.error("Cookie認証失敗:", error);
            // Cookie認証も失敗した場合はデジタルツールに遷移
            window.location.href = process.env.REACT_APP_DIGITAL_TOOL!;
          }
        }
      }
    });
    return () => {
      unSub();
    };
  }, [dispatch, token, navigate]);

  useEffect(() => {
    // 定期的にCookie更新とユーザー 情報更新処理を行う
    const intervalTime = 5 * 60 * 1000;
    setInterval(
      () => callUserAuthAfterInitialLogin(false, true, true, true),
      intervalTime,
    );
  }, []);

  // ユーザー情報更新関数
  const updateUserInfoFromAPI = async (): Promise<void> => {
    try {
      const response = await postRequest(
        process.env.REACT_APP_COMMON_API!,
        {
          "api-name": "user-info",
        },
        false,
      );

      // レスポンスデータの存在確認
      if (!response || !response.data || !response.data.dataList || response.data.dataList.length === 0) {
        console.error("ユーザー情報APIのレスポンスが空です:", response);
        throw new Error("ユーザー情報が取得できませんでした");
      }

      const json = JSON.parse(response.data.dataList[0]);

      // 権限情報をAlloyDBから取得
      let permissionCode: string[] = [];
      try {
        const authorityResponse = await fetchAuthorityInfo(json);
        permissionCode = authorityResponse?.permission_code || [];
      } catch (error) {
        console.error("権限情報の取得に失敗しました:", error);
        // 権限情報取得失敗時は空配列で続行
      }

      setUserInfo(json, permissionCode);
      console.log("ユーザー情報を更新しました:", json.employee_no);

      await syncTenpoCodes(json);
    } catch (error) {
      console.error("ユーザー情報更新エラー:", error);
      throw error;
    }
  };


  /**
   * Firebaseカスタム認証及びユーザー情報取得
   * トークン情報の再利用を防ぐために公開鍵方式を用いる
   * @param token デジタルツールにて生成されたワンタイムトークン
   */
  const callCustomFirebaseAuthentication = async (
    token: string,
  ): Promise<void> => {
    try {
      console.log("カスタム認証開始 - トークン:", token);
      const url: string =
        process.env.REACT_APP_DIGITAL_TOOLS_RUN + "auth/v1/users/user-auth";
      
      // Crypto Web APIでの公開鍵方式を用いて暗号化/複合化を行う
      const keyPair = await generateKeyPairRsaOaep();
      const publicKey = await exportPublicKey(keyPair);
      const publicKeyBase64 = arrayBufferToBase64(publicKey);
      const param = {
        user_token: token,
        public_key: publicKeyBase64,
      };
      
      const response = await postRequest(url, param, false, false);
      
      if (!response || !response.data || !response.data.data) {
        throw new Error("認証APIからの応答が無効です");
      }
      
      const obj = JSON.parse(response.data.data);
      
      if (!obj["custom_token"]) {
        throw new Error("カスタムトークンが応答に含まれていません");
      }
      
      const storageToken = await decryptRsaOaepAndConcatenate(
        obj["storage_token"],
        keyPair,
      );
      const customToken = await decryptRsaOaepAndConcatenate(
        obj["custom_token"],
        keyPair,
      );
            
      // Firebaseカスタム認証を行う
      const cAuth = getAuth();
      await signInWithCustomToken(cAuth, customToken);
      console.log("Firebase カスタム認証成功");
      
    } catch (err) {
      console.error("カスタム認証エラー:", err);
      throw err; // エラーを再スローして呼び出し元で処理
    }

  };

  /**
   * 2回目ログイン以降Cookie処理及びアプリケーション情報やユーザー情報取得
   * 一部トークンでは情報の再利用を防ぐために公開鍵方式を用いる
   * @param token デジタルツールにて生成されたワンタイムトークン
   * @returns ユーザー情報が更新されたかどうか
   */
  const callUserAuthAfterInitialLogin = async (
    isCookieAuth: boolean = false,
    isCookieUpdate: boolean = false,
    isUserInfo: boolean = false,
    isStorageToken: boolean = false,
  ): Promise<boolean> => {
    try {
      const url: string =
        process.env.REACT_APP_DIGITAL_TOOLS_RUN +
        "auth/v1/users/user-auth-after-initial-login";
      // Crypto Web APIでの公開鍵方式を用いて暗号化/複合化を行う
      const keyPair = await generateKeyPairRsaOaep();
      const publicKey = await exportPublicKey(keyPair);
      const publicKeyBase64 = arrayBufferToBase64(publicKey);
      const param = {
        public_key: publicKeyBase64,
        is_cookie_auth: isCookieAuth,
        is_cookie_update: isCookieUpdate,
        is_user_info: isUserInfo,
        is_storage_token: isStorageToken,
      };
      
      const response = await postRequest(url, param, false);
      const obj = JSON.parse(response.data.data);
      if (isCookieAuth) {
        const customToken = await decryptRsaOaepAndConcatenate(
          obj["custom_token"],
          keyPair,
        );
        // Firebaseカスタム認証を行う
        const cAuth = getAuth();
        await signInWithCustomToken(cAuth, customToken);
      }

      if (isStorageToken) {
        const storageToken = await decryptRsaOaepAndConcatenate(
          obj["storage_token"],
          keyPair,
        );
      }
      // Cookie認証でユーザー情報が更新された場合、ユーザー情報を再取得
      if (isCookieUpdate && isUserInfo) {
        await updateUserInfoFromAPI();
        return true; // ユーザー情報が更新された
      }
      
      return false; // ユーザー情報は更新されていない
      
    } catch (err) {
      console.error("Cookie認証エラー:", err);
      // firebase認証未
      if (process.env.REACT_APP_MODE === "develop") {
        // ローカルまたは開発環境ではログイン画面へ遷移する
        navigate("/login");
      } else {
        // ログイン失敗時はデジタルツールに遷移する
        window.location.href = process.env.REACT_APP_DIGITAL_TOOL!;
      }
      throw err; // エラーを再スローして呼び出し元で処理
    }
  };

  // Firebase認証が初期化されるまで待機
  if (!authInitialized) {
    return (
      <div
        style={{
          justifyContent: "center",
          display: "flex",
        }}
      >
        <CircularProgress
          size={"40vh"}
          style={{
            color: "#ccc",
            top: "30vh",
            position: "absolute",
          }}
        />
      </div>
    );
  }

  return (
    <>
      {loginLoading ? (
        <div
          style={{
            justifyContent: "center",
            display: "flex",
          }}
        >
          <CircularProgress
            size={"40vh"}
            style={{
              color: "#ccc",
              top: "30vh",
              position: "absolute",
            }}
          />
        </div>
      ) : (
        <Outlet />
      )}
    </>
  );
};

export default App;
